/**
 * Everspring — Card Image Carousel
 * Real-time drag feedback + arrows + lazy loading.
 */
(function() {
  function initCarousel(el) {
    if (el.dataset.initialized) return;
    el.dataset.initialized = '1';

    var track = el.querySelector('[data-card-track]');
    var dots = el.querySelectorAll('[data-dot-index]');
    var prevBtn = el.querySelector('[data-card-prev]');
    var nextBtn = el.querySelector('[data-card-next]');
    if (!track) return;

    var slides = track.children.length;
    if (slides < 2) return;

    var current = 0;
    var startX = 0;
    var startY = 0;
    var dragX = 0;
    var isDragging = false;
    var hasMoved = false;
    var threshold = 50;
    // Raised from 5 → 10 so a natural click with tiny mouse drift does not
    // get classified as a drag (which would swallow the click via the
    // capture-phase handler below and break card navigation).
    var moveThreshold = 10;
    var imagesLoaded = false;
    var trackWidth = 0;

    function loadLazy() {
      if (imagesLoaded) return;
      imagesLoaded = true;
      el.querySelectorAll('.es-card-carousel__img--lazy').forEach(function(img) {
        if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
        if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
        img.classList.remove('es-card-carousel__img--lazy');
      });
    }

    function goTo(index, animate) {
      if (index < 0) index = 0;
      if (index >= slides) index = slides - 1;
      if (index !== current) loadLazy();
      current = index;
      track.style.transition = animate !== false ? 'transform 0.3s ease' : 'none';
      track.style.transform = 'translateX(-' + (current * 100) + '%)';
      dots.forEach(function(d, i) { d.classList.toggle('is-active', i === current); });
    }

    function setDragOffset(dx) {
      var pct = (dx / (trackWidth || el.offsetWidth)) * 100;
      track.style.transition = 'none';
      track.style.transform = 'translateX(' + (-(current * 100) + pct) + '%)';
    }

    // Arrows
    if (prevBtn) prevBtn.addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation(); goTo(current + 1); });

    // Touch
    track.addEventListener('touchstart', function(e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      trackWidth = el.offsetWidth;
      isDragging = true;
      hasMoved = false;
    }, { passive: true });

    track.addEventListener('touchmove', function(e) {
      if (!isDragging) return;
      var dx = e.touches[0].clientX - startX;
      var dy = Math.abs(e.touches[0].clientY - startY);
      if (Math.abs(dx) > dy && Math.abs(dx) > moveThreshold) {
        hasMoved = true;
        setDragOffset(dx);
      }
    }, { passive: true });

    track.addEventListener('touchend', function(e) {
      if (!isDragging) return;
      isDragging = false;
      var diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > threshold) {
        goTo(diff > 0 ? current + 1 : current - 1);
      } else {
        goTo(current);
      }
    }, { passive: true });

    // Mouse drag
    // NOTE: we intentionally do NOT call e.preventDefault() in mousedown.
    // Previously that was done to suppress native image drag, but it also
    // interfered with the click-to-navigate flow on the <a> slides in some
    // browsers. Image dragging is already disabled via draggable="false" on
    // the <img> + pointer-events:none in CSS, so preventDefault is redundant.
    track.addEventListener('mousedown', function(e) {
      startX = e.clientX;
      trackWidth = el.offsetWidth;
      isDragging = true;
      hasMoved = false;
    });

    document.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > moveThreshold) {
        hasMoved = true;
        setDragOffset(dx);
      }
    });

    document.addEventListener('mouseup', function(e) {
      if (!isDragging) return;
      isDragging = false;
      var diff = startX - e.clientX;
      if (Math.abs(diff) > threshold) {
        goTo(diff > 0 ? current + 1 : current - 1);
      } else {
        goTo(current);
      }
    });

    // Prevent click on drag
    track.addEventListener('click', function(e) {
      if (hasMoved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // Dots
    dots.forEach(function(dot) {
      dot.addEventListener('click', function(e) {
        e.preventDefault(); e.stopPropagation();
        goTo(parseInt(this.getAttribute('data-dot-index')));
      });
    });

    // Preload all images on first hover (before user swipes)
    el.addEventListener('mouseenter', loadLazy, { once: true });
    el.addEventListener('touchstart', loadLazy, { once: true, passive: true });

    if ('ontouchstart' in window) {
      var dc = el.querySelector('[data-card-dots]');
      if (dc) dc.classList.add('is-visible');
    }
  }

  function initAll() {
    document.querySelectorAll('[data-card-carousel]').forEach(initCarousel);
  }

  /**
   * Peek-animation onboarding hint.
   * On every collection page visit on a touch device, the first visible
   * product card's carousel briefly slides ~22% to the left and back,
   * hinting that the images are swipeable. Skipped on desktop and when the
   * user prefers reduced motion. Runs on every visit (no storage guard).
   */
  function initPeekHint() {
    // Only on touch devices (no hover support → likely touch).
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    // Respect reduced motion preference.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var firstCarousel = document.querySelector('[data-card-carousel]');
    if (!firstCarousel) return;

    // Force-load ALL lazy images in the first carousel immediately, so the
    // second image is actually decoded by the time the peek animation runs.
    // Otherwise the user sees a lazy-load placeholder / alt text instead of
    // the photo during the peek. This is a stronger version of the existing
    // lazy-load logic which only kicks in on user interaction.
    var lazyImgs = firstCarousel.querySelectorAll('.es-card-carousel__img--lazy');
    lazyImgs.forEach(function (img) {
      if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
      if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
      img.classList.remove('es-card-carousel__img--lazy');
    });

    // Find the second slide's <img> — that's the one that becomes visible
    // during the peek, so we wait for it to finish loading before animating.
    var track = firstCarousel.querySelector('[data-card-track]');
    var secondSlide = track && track.children[1];
    var secondImg = secondSlide && secondSlide.querySelector('img');

    var triggerAnim = function () {
      firstCarousel.classList.add('is-peek-hint');
      // Cleanup after animation finishes (CSS duration is 2200ms).
      setTimeout(function () {
        firstCarousel.classList.remove('is-peek-hint');
      }, 2400);
    };

    var runHint = function () {
      if (!secondImg || secondImg.complete) {
        triggerAnim();
        return;
      }
      // Wait for the second image to actually load before starting the
      // animation. Hard 1500ms fallback in case 'load' never fires.
      var done = false;
      var ready = function () {
        if (done) return;
        done = true;
        triggerAnim();
      };
      secondImg.addEventListener('load', ready, { once: true });
      secondImg.addEventListener('error', ready, { once: true });
      setTimeout(ready, 1500);
    };

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            io.disconnect();
            // Small delay so it runs after layout settles and after the user
            // has started to notice the grid.
            setTimeout(runHint, 600);
          }
        });
      }, { threshold: 0.6 });
      io.observe(firstCarousel);
    } else {
      setTimeout(runHint, 800);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      initPeekHint();
    });
  } else {
    initAll();
    initPeekHint();
  }

  var grid = document.getElementById('ProductGridContainer');
  if (grid) {
    new MutationObserver(function() { setTimeout(initAll, 100); }).observe(grid, { childList: true, subtree: true });
  }
})();
